import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

// Keep this list aligned with the venue groups in main-collection.liquid.
const eastVenues = new Set([
      'adams-beach',
      'ammos-beach',
      'aphrodite-beach',
      'asterias-beach',
      'dome-beach',
      'grecian-sands',
      'nissi-beach',
      'olympic-lagoon-ayia-napa',
      'the-legacy-venue',
      'amathus-beach',
      'columbia-beach-resort',
      'four-seasons-limassol',
      'honeyli-hill',
      'park-lane-luxury-resort',
      'st-raphael',
      'yermasoyia-cultural-centre',
      'nissiblu',
      'kaliva-on-the-beach',
      'ayia-triada-beach',
      'constantinos-the-great',
      'capo-bay',
      'golden-coast',
      'marlita',
      'pernera-blue-waters',
      'st-elias-gardens',
      'vrissiana-beach',
      'cape-greco',
      'nissaki-taverna',
      'sirines-beach',
      'thalassines-beach-villas',
      'adams-beach-hotel-with-beach-ceremony',
      'asterias-beach-hotel-with-beach-ceremony',
      'atlantica-sungarden-beach',
      'grecian-sands-with-beach-ceremony',
      'nissi-beach-resort-with-beach-ceremony',
      'mare-ayia-napa',
      'olympic-lagoon-ayia-napa-with-beach-ceremony',
      'amara',
      'city-of-dreams-mediterranean',
      'sunrise-beach-hotel',
      'sunrise-jade-hotel',
      'sunrise-pearl-hotel-spa',
      'mediterranean-beach',
      'grecian-park-hotel'

    ]);
const westVenues = new Set([
      'aghia-kyriaki',
      'ayia-kyriaki',
      'alexander-the-great',
      'almyra',
      'amavi',
      'annabelle',
      'antasia-beach-restaurant',
      'athena-beach',
      'atlantida-beach-bar',
      'base-venue',
      'coral-beach',
      'coral-residences',
      'cynthiana-hotel',
      'elea-estate',
      'elysium',
      'ivi-mare',
      'kefalos-beach',
      'king-evelthon-beach-hotel',
      'ktima-alassos',
      'l-chataeu',
      'l-chateau',
      'leonardo-cypria-bay',
      'leonardo-laura-beach',
      'mayfair-hotel-gardens',
      'minthis-resort',
      'oasis-gardens',
      'olympic-lagoon-paphos',
      'ottoman-baths',
      'palio-monastiro',
      'paphos-beach',
      'paphos-town-hall',
      'peyia-village',
      'sea-caves',
      'st-nicholas-church',
      'st-georges',
      'thalassa',
      'vasilias-nikoklis-inn',
      'vrysi-of-lemba',
      'yacht-wedding-paphos',
      'yeroskipou-town-hall',
      'anassa',
      'aphrodite-hills-villas',
      'alexander-the-great-beach-hotel-with-beach-ceremony',
      'almyra-with-beach-ceremony',
      'amavi-madefortwo-with-beach-ceremony',
      'annabelle-with-beach-ceremony',
      'aquamare-beach-hotel-spa',
      'aquamare-beach-hotel-spa-with-beach-ceremony',
      'athena-beach-hotel-with-beach-ceremony',
      'coral-beach-hotel-resort-with-beach-ceremony',
      'cynthiana-beach-hotel-with-beach-ceremony',
      'elysium-with-beach-ceremony',
      'imperial-island-resort',
      'imperial-island-resort-with-beach-ceremony',
      'ivi-mare-with-beach-ceremony',
      'leonardo-cypria-bay-with-beach-ceremony',
      'mayfair-gardens-with-beach-ceremony',
      'mayfair-hotel-with-beach-ceremony',
      'olympic-lagoon-resort-paphos-with-beach-ceremony',
      'st-george-hotel-spa-beach-resort-with-beach-ceremony',
      'thalassa-boutique-resort-with-beach-ceremony',
      'leonardo-cypria-maris',
      'liopetro'
    ]);

// Regional and individual exclusions override every inclusion tag.
function isVenueExcluded(tags, venue) {
  return tags.has('venue-exclude-' + venue) ||
    (eastVenues.has(venue) && tags.has('venue-exclude-all-east-venues')) ||
    (westVenues.has(venue) && tags.has('venue-exclude-all-west-venues'));
}

class ProductRecommendations extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
      if (mutation.attributeName === 'data-recommendations-performed' && this.dataset.recommendationsPerformed === 'true') continue;
      this.#loadRecommendations();
      break;
    }
  });

  #contentObserver = new MutationObserver(() => this.#filterRecommendations());
  #cachedRecommendations = {};
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    const content = this.querySelector('.section-resource-list');
    if (content) content.style.visibility = 'hidden';
    const params = new URLSearchParams(window.location.search);
    const venue = (params.get('venue') || '').toLowerCase();
    const year = params.get('wedding-year') || '';
    // Fail closed when the product was not reached with a valid venue and year.
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(venue) || !/^20\d{2}$/.test(year)) {
      if (content) content.style.display = 'none';
      return;
    }
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#contentObserver.observe(this, { childList: true, subtree: true });
    if (this.dataset.recommendationsPerformed === 'true') this.#filterRecommendations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#contentObserver.disconnect();
  }

  #filterRecommendations() {
    if (this.dataset.recommendationsPerformed !== 'true') return;
    const params = new URLSearchParams(window.location.search);
    const venue = (params.get('venue') || '').toLowerCase();
    const year = params.get('wedding-year') || '';
    const content = this.querySelector('.section-resource-list');
    if (!content) return;
    let shown = 0;
    for (const item of this.querySelectorAll('.resource-list__item')) {
      let tags = [];
      try { tags = JSON.parse(item.dataset.venueRecommendationTags || '[]'); }
      catch (_) { /* Missing or invalid tags: keep hidden. */ }
      const eligibleTags = new Set(tags.map((tag) => String(tag).toLowerCase()));
      const eligible = eligibleTags.has(`year-${year}`) &&
        !isVenueExcluded(eligibleTags, venue) &&
        (eligibleTags.has(venue) || eligibleTags.has('venue-all-cyprus') ||
         (eastVenues.has(venue) && eligibleTags.has('venue-all-east-cyprus')) ||
         (westVenues.has(venue) && eligibleTags.has('venue-all-west-cyprus')));
      item.style.display = eligible ? '' : 'none';
      if (!eligible) continue;
      shown++;
      for (const link of item.querySelectorAll('a[href*="/products/"]')) {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('venue', venue);
        url.searchParams.set('wedding-year', year);
        link.href = url.toString();
      }
    }
    content.style.display = shown ? '' : 'none';
    content.style.visibility = shown ? 'visible' : 'hidden';
    const background = this.querySelector('.section-background');
    if (background) background.style.display = shown ? '' : 'none';
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    if (!productId || !sectionId) throw new Error('Product ID and a section ID are required');
    if (recommendationsPerformed === 'true') return;
    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) this.#handleError(new Error(`Server returned ${result.status}`));
          return;
        }
        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          requestAnimationFrame(() => this.#filterRecommendations());
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => this.#handleError(e));
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) return { success: true, data: cachedResponse };
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) return { success: false, status: response.status };
      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
